import React, { useEffect, useState, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import {
  CreditCard, CheckCircle, AlertCircle, Loader2, Link2, Unlink,
  ArrowRight, ExternalLink, Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const StripePayments = () => {
  const [status, setStatus]         = useState(null); // null=loading
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/stripe-connect/status');
      setStatus(res.data);
    } catch {
      setStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    loadStatus();

    // Handle return from Stripe onboarding
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_return') === '1' || params.get('stripe_refresh') === '1') {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.post('/stripe-connect/start', {
        origin_url: window.location.origin,
      });
      window.location.href = res.data.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Stripe setup — please try again');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect your Stripe account?\n\nThis only removes the link from NexusCMS — your Stripe account will not be deleted. Ticket sales will fall back to the platform account until you reconnect.'
    )) return;
    setDisconnecting(true);
    try {
      await api.delete('/stripe-connect/disconnect');
      toast.success('Stripe account disconnected');
      setStatus({ connected: false });
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  const isLoading = status === null;
  const isActive  = status?.charges_enabled;
  const isPending = status?.connected && !status?.charges_enabled;

  return (
    <div className="flex" data-testid="stripe-payments-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center text-white">
            <CreditCard size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Stripe Payments</h1>
            <p className="text-sm text-slate-500">Connect your Stripe account to receive event ticket sales directly.</p>
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="animate-spin" size={20} />
          </div>
        )}

        {!isLoading && (
          <div className="max-w-2xl space-y-6">

            {/* Status card */}
            <div className={`rounded-xl border p-6 ${
              isActive  ? 'bg-emerald-50 border-emerald-200' :
              isPending ? 'bg-amber-50 border-amber-200' :
              'bg-white border-slate-200'
            }`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  isActive  ? 'bg-emerald-100' :
                  isPending ? 'bg-amber-100' :
                  'bg-slate-100'
                }`}>
                  {isActive  ? <CheckCircle size={20} className="text-emerald-600" /> :
                   isPending ? <Loader2 size={20} className="text-amber-500 animate-spin" /> :
                   <CreditCard size={20} className="text-slate-400" />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="font-semibold text-slate-900 text-base">
                      {isActive  ? 'Stripe account connected' :
                       isPending ? 'Completing Stripe setup…' :
                       'No Stripe account connected'}
                    </h2>
                    {isActive && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid="stripe-connect-active-badge">
                        Active
                      </Badge>
                    )}
                    {isPending && (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="stripe-connect-pending-badge">
                        Pending
                      </Badge>
                    )}
                  </div>

                  <p className="text-sm text-slate-500 mt-1">
                    {isActive
                      ? 'Ticket sales go directly to your bank account. NexusCMS deducts a 1% platform fee per transaction.'
                      : isPending
                      ? 'Your Stripe account is being set up. If you haven\'t finished the sign-up, click "Continue setup" to complete it.'
                      : 'Connect a Stripe account to receive ticket payments directly. New or existing Stripe accounts are both supported.'}
                  </p>

                  {isActive && status?.account_id && (
                    <p className="text-xs text-slate-400 mt-1.5 font-mono" data-testid="stripe-connect-account-id">
                      Account: {status.account_id}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-3 mt-4">
                    {!status?.connected && (
                      <Button
                        onClick={handleConnect}
                        disabled={connecting}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="stripe-connect-btn"
                      >
                        {connecting
                          ? <><Loader2 size={15} className="animate-spin mr-2" /> Redirecting to Stripe…</>
                          : <><Link2 size={15} className="mr-2" /> Connect Stripe Account</>}
                      </Button>
                    )}
                    {isPending && (
                      <Button
                        onClick={handleConnect}
                        disabled={connecting}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="stripe-connect-resume-btn"
                      >
                        {connecting
                          ? <><Loader2 size={15} className="animate-spin mr-2" /> Redirecting…</>
                          : <><ArrowRight size={15} className="mr-2" /> Continue Setup</>}
                      </Button>
                    )}
                    {status?.connected && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDisconnect}
                        disabled={disconnecting}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        data-testid="stripe-connect-disconnect-btn"
                      >
                        {disconnecting
                          ? <Loader2 size={14} className="animate-spin mr-1.5" />
                          : <Unlink size={14} className="mr-1.5" />}
                        Disconnect
                      </Button>
                    )}
                    {status?.connected && (
                      <Button variant="outline" size="sm" onClick={loadStatus} data-testid="stripe-connect-refresh-btn">
                        <Loader2 size={13} className="mr-1.5" /> Refresh Status
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* How it works */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-semibold text-slate-800 text-sm mb-4 flex items-center gap-2">
                <Info size={15} className="text-slate-400" /> How it works
              </h3>
              <div className="space-y-3">
                {[
                  { step: '1', title: 'Connect your Stripe account', desc: 'Click "Connect Stripe Account" above. You can create a new Stripe account or link an existing one. Stripe handles all identity verification.' },
                  { step: '2', title: 'Enable tickets on your events', desc: 'In the Events module, toggle "Sell tickets via Stripe" on any event and set your ticket price.' },
                  { step: '3', title: 'Collect direct payouts', desc: 'Buyers pay via Stripe Checkout. Funds go directly to your bank account. NexusCMS deducts a flat 1% platform fee automatically.' },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {step}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">{title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Fee info */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <h3 className="font-semibold text-slate-700 text-sm mb-2">Platform fee</h3>
              <p className="text-sm text-slate-500">
                NexusCMS charges a flat <strong className="text-slate-700">1% platform fee</strong> on each ticket sale processed via your connected Stripe account.
                This is deducted automatically — you receive the remaining 99% (minus Stripe's own processing fees).
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Example: $50 ticket → you receive $49.50 − Stripe fees. No monthly fees. No hidden charges.
              </p>
            </div>

            {/* Stripe dashboard link */}
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <ExternalLink size={14} className="shrink-0" />
              <a
                href="https://dashboard.stripe.com"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-slate-700"
                data-testid="stripe-dashboard-link"
              >
                Open Stripe Dashboard
              </a>
              <span>to view payouts, transactions, and account settings.</span>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default StripePayments;
